import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AnalyticsEventType } from './analytics.service';

export interface AnalyticsUserSummary {
  publicId: string;
  name: string;
  email: string;
  sessions: number;
  pageViews: number;
  clicks: number;
  focuses: number;
  scrolls: number;
  formSubmits: number;
  outboundActions: number;
  mediaInteractions: number;
  contentViews: number;
  activeDurationMs: number;
  lastActivityAt: string;
}

export interface AnalyticsEventRecord {
  publicId: string;
  type: AnalyticsEventType;
  pagePath: string;
  elementKind: string | null;
  elementLabel: string | null;
  scrollPercent: number | null;
  durationMs: number | null;
  occurredAt: string;
}

export interface AnalyticsVisitorSummary {
  visitorId: string;
  sessions: number;
  pageViews: number;
  clicks: number;
  focuses: number;
  scrolls: number;
  formSubmits: number;
  outboundActions: number;
  mediaInteractions: number;
  contentViews: number;
  activeDurationMs: number;
  lastActivityAt: string;
}

export interface AnalyticsApiError { code: number; message: string; }

@Injectable({ providedIn: 'root' })
export class AnalyticsApiService {
  private readonly apiUrl = environment.apiUrl;

  constructor(private readonly http: HttpClient) {}

  getOwnerUsers(filters: { search?: string; eventType?: string; sort?: string; from?: string; to?: string }): Observable<AnalyticsUserSummary[]> {
    let params = new HttpParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        params = params.set(key, value);
      }
    });
    return this.http.get<{ success: boolean; users: AnalyticsUserSummary[] }>(`${this.apiUrl}/analytics/owner/users`, { params })
      .pipe(map((response) => response.users), catchError(this.handleError));
  }

  getOwnerUser(userId: string): Observable<{ summary: AnalyticsUserSummary; events: AnalyticsEventRecord[] }> {
    return this.http.get<{ success: boolean; summary: AnalyticsUserSummary; events: AnalyticsEventRecord[] }>(`${this.apiUrl}/analytics/owner/users/${userId}`)
      .pipe(map(({ summary, events }) => ({ summary, events })), catchError(this.handleError));
  }

  getWebsiteVisitors(websiteId: string): Observable<AnalyticsVisitorSummary[]> {
    return this.http.get<{ success: boolean; visitors: AnalyticsVisitorSummary[] }>(`${this.apiUrl}/analytics/websites/${websiteId}/visitors`)
      .pipe(map((response) => response.visitors), catchError(this.handleError));
  }

  getWebsiteVisitor(websiteId: string, visitorId: string): Observable<AnalyticsEventRecord[]> {
    return this.http.get<{ success: boolean; events: AnalyticsEventRecord[] }>(`${this.apiUrl}/analytics/websites/${websiteId}/visitors/${visitorId}`)
      .pipe(map((response) => response.events), catchError(this.handleError));
  }

  private handleError = (error: HttpErrorResponse): Observable<never> => {
    const message = error.error?.error?.message || error.message || 'Analytics could not be loaded.';
    return throwError(() => ({ code: error.status, message } as AnalyticsApiError));
  };
}
